import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw, Download } from "lucide-react";

const CREAM = "#FDFBF7";
const FOREST = "#1f3d32";

// 9:16 phone frame wrapper
const PhoneFrame = ({ children, bg = CREAM }: { children: React.ReactNode; bg?: string }) => (
  <div
    className="relative w-full overflow-hidden rounded-[36px] shadow-2xl border border-black/5"
    style={{ aspectRatio: "9 / 16", background: bg }}
  >
    {children}
  </div>
);

// ============ Variant 1: "Reveal" ============
const VariantReveal = ({ cycle }: { cycle: number }) => (
  <PhoneFrame bg={CREAM}>
    <div key={cycle} className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="font-cormorant italic text-2xl tablet:text-3xl mb-6"
        style={{ color: FOREST, opacity: 0.7 }}
      >
        introducing
      </motion.div>

      <div className="overflow-hidden">
        <motion.h1
          initial={{ y: "110%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 0.9, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="font-heading font-bold leading-none tracking-tight"
          style={{ color: FOREST, fontSize: "clamp(64px, 18vw, 140px)" }}
        >
          qwip
        </motion.h1>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.7 }}
        className="mt-10 font-heading text-xl tablet:text-2xl font-medium"
        style={{ color: FOREST }}
      >
        now live
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 2.1 }}
        className="font-heading text-xl tablet:text-2xl font-medium"
        style={{ color: FOREST }}
      >
        Westfield White City
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: [0, 1.4, 1] }}
        transition={{ duration: 0.8, delay: 2.7 }}
        className="mt-10 w-2 h-2 rounded-full"
        style={{ background: FOREST }}
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ delay: 3.2 }}
        className="absolute bottom-8 left-0 right-0 text-center text-xs tracking-[0.3em] uppercase font-body"
        style={{ color: FOREST }}
      >
        qwip.co.uk
      </motion.div>
    </div>
  </PhoneFrame>
);

// ============ Variant 2: "Pod drop" ============
const PodSilhouette = () => (
  <svg viewBox="0 0 200 280" className="w-full h-full" fill="none">
    <rect x="20" y="20" width="160" height="240" rx="24" fill={CREAM} opacity="0.95" />
    <rect x="20" y="20" width="160" height="240" rx="24" stroke={CREAM} strokeOpacity="0.4" />
    <circle cx="150" cy="140" r="6" fill={FOREST} />
    <rect x="40" y="50" width="80" height="6" rx="3" fill={FOREST} opacity="0.3" />
    <rect x="40" y="64" width="50" height="6" rx="3" fill={FOREST} opacity="0.2" />
  </svg>
);

const VariantPodDrop = ({ cycle }: { cycle: number }) => (
  <PhoneFrame bg={FOREST}>
    <div key={cycle} className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center">
      <motion.div
        initial={{ y: "-120%", rotate: -8 }}
        animate={{ y: "0%", rotate: 0 }}
        transition={{ type: "spring", stiffness: 90, damping: 12, delay: 0.2 }}
        className="w-40 h-56 tablet:w-48 tablet:h-64"
      >
        <PodSilhouette />
      </motion.div>

      <motion.div
        initial={{ scaleX: 0.3, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 0.4 }}
        transition={{ delay: 1.1, duration: 0.5 }}
        className="mt-2 h-2 w-32 rounded-full blur-md"
        style={{ background: "black" }}
      />

      <div className="mt-10 h-16 flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={`a-${cycle}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4, delay: 1.4 }}
            className="font-heading text-2xl tablet:text-3xl font-medium"
            style={{ color: CREAM }}
          >
            your private pod.
          </motion.div>
        </AnimatePresence>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.6 }}
        className="font-heading text-xl tablet:text-2xl font-medium mt-2"
        style={{ color: CREAM, opacity: 0.85 }}
      >
        now at Westfield White City
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.4 }}
        className="absolute bottom-10 left-0 right-0 flex flex-col items-center"
      >
        <div className="h-px w-12 mb-3" style={{ background: CREAM, opacity: 0.3 }} />
        <div className="font-heading text-2xl font-bold" style={{ color: CREAM }}>qwip</div>
      </motion.div>
    </div>
  </PhoneFrame>
);

// ============ Variant 3: "Ticker / news flash" ============
const ROTATING_WORDS = ["work.", "focus.", "call.", "breathe.", "qwip."];

const VariantTicker = ({ cycle }: { cycle: number }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
    const ids: number[] = [];
    ROTATING_WORDS.forEach((_, i) => {
      ids.push(window.setTimeout(() => setIdx(i), 300 + i * 500));
    });
    return () => ids.forEach(clearTimeout);
  }, [cycle]);

  return (
    <PhoneFrame bg={CREAM}>
      <div key={cycle} className="absolute inset-0 flex flex-col">
        {/* top 60% */}
        <div className="flex-[3] flex items-center justify-center px-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${cycle}-${idx}`}
              initial={{ opacity: 0, y: 16, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -16, filter: "blur(8px)" }}
              transition={{ duration: 0.25 }}
              className="font-heading font-bold leading-none tracking-tight"
              style={{ color: FOREST, fontSize: "clamp(56px, 16vw, 120px)" }}
            >
              {ROTATING_WORDS[idx]}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* bottom 40% */}
        <div className="flex-[2] flex flex-col justify-center overflow-hidden" style={{ background: FOREST }}>
          <div className="relative overflow-hidden py-4">
            <motion.div
              initial={{ x: "0%" }}
              animate={{ x: "-50%" }}
              transition={{ duration: 8, ease: "linear", repeat: Infinity }}
              className="flex whitespace-nowrap font-heading text-xl tablet:text-2xl font-bold tracking-wide"
              style={{ color: CREAM }}
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <span key={i} className="px-6">
                  NOW LIVE AT WESTFIELD WHITE CITY ·
                </span>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3, duration: 0.5 }}
            className="text-center mt-4 font-body text-sm tracking-[0.3em] uppercase"
            style={{ color: CREAM, opacity: 0.85 }}
          >
            book at qwip.co.uk
          </motion.div>
        </div>
      </div>
    </PhoneFrame>
  );
};

// ============ Page ============
const VARIANTS = [
  { id: "reveal", file: "qwip-ad-reveal.mp4", title: "01 · Reveal", desc: "Typographic and minimal. Cormorant intro, big wordmark wipe, clean lockup.", Comp: VariantReveal, duration: 5000 },
  { id: "drop", file: "qwip-ad-poddrop.mp4", title: "02 · Pod Drop", desc: "Playful. Pod springs in on forest green, copy lands beside it.", Comp: VariantPodDrop, duration: 5500 },
  { id: "ticker", file: "qwip-ad-ticker.mp4", title: "03 · News Flash", desc: "Editorial. Rotating words snap to qwip, marquee announces the launch.", Comp: VariantTicker, duration: 6000 },
];

const VariantCard = ({ v }: { v: typeof VARIANTS[number] }) => {
  const [cycle, setCycle] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setCycle((c) => c + 1), v.duration + 800);
    return () => clearInterval(id);
  }, [v.duration]);

  const Comp = v.Comp;
  return (
    <div className="flex flex-col gap-4">
      <Comp cycle={cycle} />
      <div className="flex items-start justify-between gap-4 px-1">
        <div>
          <h3 className="font-heading text-lg font-bold" style={{ color: FOREST }}>{v.title}</h3>
          <p className="font-body text-sm text-muted-foreground mt-1 max-w-xs">{v.desc}</p>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <a
            href={`/ads/${v.file}`}
            download
            className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-medium px-3 py-2 rounded-full hover:opacity-90 transition-opacity"
            style={{ background: FOREST, color: CREAM }}
          >
            <Download className="w-3 h-3" strokeWidth={2} />
            MP4
          </a>
          <button
            onClick={() => setCycle((c) => c + 1)}
            className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-medium px-3 py-2 rounded-full border hover:bg-black/5 transition-colors"
            style={{ color: FOREST, borderColor: `${FOREST}33` }}
          >
            <RotateCcw className="w-3 h-3" strokeWidth={2} />
            Replay
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Ad() {
  return (
    <div className="min-h-[100dvh]" style={{ background: CREAM }}>
      <div className="max-w-[1400px] mx-auto px-6 tablet:px-10 py-10 tablet:py-16">
        <header className="mb-10 tablet:mb-16 flex flex-col gap-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-medium" style={{ color: FOREST, opacity: 0.6 }}>
            <span>qwip</span>
            <span>·</span>
            <span>ad previews</span>
          </div>
          <h1 className="font-heading text-3xl tablet:text-5xl font-bold leading-tight" style={{ color: FOREST }}>
            now live at <span className="font-cormorant italic font-normal">Westfield White City</span>
          </h1>
          <p className="font-body text-sm tablet:text-base text-muted-foreground max-w-xl">
            Three short concepts ready for Instagram Reels or Stories. Tap MP4 to download a 1080 × 1920 video, or screen-record any frame.
          </p>
        </header>

        <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-3 gap-8 tablet:gap-10">
          {VARIANTS.map((v) => (
            <VariantCard key={v.id} v={v} />
          ))}
        </div>

        <footer className="mt-16 text-xs tracking-wider uppercase text-muted-foreground text-center">
          1080 × 1920 · 30 fps · muted · ready for Instagram
        </footer>
      </div>
    </div>
  );
}
